import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  FormContainer,
  NextButton,
  Prompt,
  QuestionContainer,
  QuestionPageContainer,
  RadioInput,
  LaurenQuoteMobile,
  StringInput,
} from "~/OnboardingApp/components";
import { useOnboarding } from "~/OnboardingApp/hooks/useOnboarding";
import { useSubmitOnboardingQuestion } from "~/OnboardingApp/hooks/useSubmitOnboardingQuestion";
import { requiredString } from "~/utils/validations";

export type CoverageLevel =
  | ""
  | "Yes, but super minimal"
  | "Yes, a few rounds of IVF"
  | "Nope, I’m the 75%!"
  | "Not sure"
  | "Other";

type Values = {
  fertilityCoverage: CoverageLevel;
  fertilityCoverageOther: string;
};

const values: CoverageLevel[] = [
  "Yes, but super minimal",
  "Yes, a few rounds of IVF",
  "Nope, I’m the 75%!",
  "Not sure",
  "Other",
];

const validationSchema = Yup.object({
  fertilityCoverage: requiredString.oneOf(values),
  fertilityCoverageOther: Yup.string(),
});

export const CoveragePage: FC = () => {
  const { t } = useTranslation();
  const { fertilityCoverage, fertilityCoverageOther } = useOnboarding();
  const { onSubmit } = useSubmitOnboardingQuestion();

  const initialValues: Values = {
    fertilityCoverage: fertilityCoverage || "",
    fertilityCoverageOther: fertilityCoverageOther || "",
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
    validateOnMount: true,
  });

  const showAdditionalQuestion = formik.values.fertilityCoverage === "Other";
  const isAutoAdvance = (option: string) => option !== "Other";
  const showButton = showAdditionalQuestion || !!fertilityCoverage;

  return (
    <QuestionPageContainer>
      <FormContainer formik={formik}>
        <QuestionContainer>
          <Prompt
            comment={t("onboarding.coveragePage.comment")}
            question={t("onboarding.coveragePage.question")}
          />
          <LaurenQuoteMobile>
            {t("onboarding.coveragePage.comment")}
          </LaurenQuoteMobile>

          <RadioInput
            autoAdvance={isAutoAdvance}
            defaultValue={fertilityCoverage}
            id="fertilityCoverage"
            values={values}
          />
        </QuestionContainer>

        {showAdditionalQuestion && (
          <QuestionContainer my={5}>
            <Prompt
              question={t(
                "onboarding.coveragePage.additionalQuestion.question",
              )}
            />

            <StringInput
              id="fertilityCoverageOther"
              placeholder={t(
                "onboarding.coveragePage.additionalQuestion.placeholder",
              )}
            />
          </QuestionContainer>
        )}

        <NextButton showButton={showButton} />
      </FormContainer>
    </QuestionPageContainer>
  );
};
